import { z } from 'zod'
import type { Manager, ManagerId, PipelineId } from '@ludus/core'
import { apiClient } from './client.ts'
import { castManager, classifyApiError, managerSchema } from './pipelines.ts'

const managersEnvelopeSchema = z.object({ items: z.array(managerSchema) })
const managerEnvelopeSchema = z.object({ manager: managerSchema })

/** Mirrors `GET /api/managers/:id/merge/:absorbedId`: what folding the second into the first would move. */
const mergePreviewSchema = z.object({
  plan: z.object({
    kept: z.object({ isActive: z.boolean() }),
    identities: managerSchema.shape.identities,
    conflicts: z.array(z.string()),
    pipelinesAdded: z.array(z.string()),
    extensionsAdded: z.array(z.string()),
    frozen: z.object({ phone: z.string().nullable(), email: z.string().nullable() }),
  }),
  cards: z.number(),
  schedules: z.number(),
})

export type ManagerMergePreview = z.infer<typeof mergePreviewSchema>

/** Mirrors `GET /api/managers`: every manager of the tenant that is not deleted. */
export async function listManagers(): Promise<Manager[]> {
  try {
    const { data } = await apiClient.get('/managers')
    return managersEnvelopeSchema.parse(data).items.map(castManager)
  } catch (err) {
    throw classifyApiError(err)
  }
}

/** The declared fields of `PATCH /api/managers/:id`; `pipelineIds` and `phoneExtensions` replace the whole list. */
export interface ManagerPatch {
  firstName?: string
  lastName?: string
  phone?: string | null
  email?: string | null
  pipelineIds?: PipelineId[]
  phoneExtensions?: string[]
  isActive?: boolean
}

export interface NewManager {
  firstName: string
  lastName?: string
}

export async function createManager(input: NewManager): Promise<Manager> {
  try {
    const { data } = await apiClient.post('/managers', input)
    return castManager(managerEnvelopeSchema.parse(data).manager)
  } catch (err) {
    throw classifyApiError(err)
  }
}

export async function updateManager(id: ManagerId, patch: ManagerPatch): Promise<Manager> {
  try {
    const { data } = await apiClient.patch(`/managers/${id}`, patch)
    return castManager(managerEnvelopeSchema.parse(data).manager)
  } catch (err) {
    throw classifyApiError(err)
  }
}

export async function describeMerge(
  keepId: ManagerId,
  absorbedId: ManagerId,
): Promise<ManagerMergePreview> {
  try {
    const { data } = await apiClient.get(`/managers/${keepId}/merge/${absorbedId}`)
    return mergePreviewSchema.parse(data)
  } catch (err) {
    throw classifyApiError(err)
  }
}

export async function mergeManagers(keepId: ManagerId, absorbedId: ManagerId): Promise<Manager> {
  try {
    const { data } = await apiClient.post(`/managers/${keepId}/merge`, { absorbedId })
    return castManager(managerEnvelopeSchema.parse(data).manager)
  } catch (err) {
    throw classifyApiError(err)
  }
}
